import { useState } from 'react'
import { useAdminUsers, useToggleAdminStatus, useAdminTiers, useAssignUserTier } from '@/hooks/useAdminUsers'
import { useUpdateAccessStatus } from '@/hooks/useAdminWaitlist'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, Mail, Calendar, Shield, ShieldOff, Clock, CheckCircle2, Ban, CreditCard } from 'lucide-react'
import { toast } from 'sonner'
import type { AccessStatus } from '@/hooks/useAccessStatus'

export function AdminUsersManager() {
  const [searchQuery, setSearchQuery] = useState('')

  const { data: users, isLoading, error } = useAdminUsers()
  const { data: tiers } = useAdminTiers()
  const toggleAdminMutation = useToggleAdminStatus()
  const updateAccessStatusMutation = useUpdateAccessStatus()
  const assignTierMutation = useAssignUserTier()

  const handleToggleAdmin = async (userId: string, currentIsAdmin: boolean, email: string) => {
    const action = currentIsAdmin ? 'demote from' : 'promote to'

    try {
      await toggleAdminMutation.mutateAsync({
        userId,
        isAdmin: !currentIsAdmin,
      })
      toast.success(`Successfully ${action} admin: ${email}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to ${action} admin: ${errorMessage}`)
    }
  }

  const handleAccessStatusChange = async (userId: string, newStatus: AccessStatus, email: string) => {
    try {
      await updateAccessStatusMutation.mutateAsync({
        userId,
        accessStatus: newStatus,
      })
      toast.success(`Updated access status for ${email} to ${newStatus}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to update access status: ${errorMessage}`)
    }
  }

  const handleTierChange = async (userId: string, tierName: string, email: string) => {
    try {
      await assignTierMutation.mutateAsync({ userId, tierName })
      const tier = tiers?.find(t => t.name === tierName)
      toast.success(`Updated ${email} to ${tier?.display_name || tierName} tier`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to update tier: ${errorMessage}`)
    }
  }

  const filteredUsers = users?.filter((user) => {
    const query = searchQuery.toLowerCase()
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    )
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-medium">Failed to load users</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
      </div>
    )
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No users found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="space-y-2">
        <Label htmlFor="user-search">Search Users</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="user-search"
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Total Users</p>
          <p className="text-2xl font-bold">{users.length}</p>
        </div>
        <div className="border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Active Projects</p>
          <p className="text-2xl font-bold">
            {users.reduce((sum, user) => sum + user.project_count, 0)}
          </p>
        </div>
        <div className="border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Avg Projects/User</p>
          <p className="text-2xl font-bold">
            {(users.reduce((sum, user) => sum + user.project_count, 0) / users.length).toFixed(1)}
          </p>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-2">
        {filteredUsers?.map((user) => (
          <div
            key={user.id}
            className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user.full_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{user.full_name || 'No name set'}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {user.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <Badge variant="secondary">
                    {user.project_count} {user.project_count === 1 ? 'project' : 'projects'}
                  </Badge>
                  {user.pricing_tier && (
                    <Badge variant={user.pricing_tier.name === 'beta' ? 'default' : 'outline'} className="flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      {user.pricing_tier.display_name}
                    </Badge>
                  )}
                  {user.is_admin && (
                    <Badge variant="default" className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      Admin
                    </Badge>
                  )}
                  {user.access_status === 'active' && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Active
                    </Badge>
                  )}
                  {user.access_status === 'waitlist' && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Waitlist
                    </Badge>
                  )}
                  {user.access_status === 'suspended' && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <Ban className="h-3 w-3" />
                      Suspended
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Joined {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className="text-xs text-muted-foreground">
                  ID: {user.id.slice(0, 8)}...
                </div>
                <div className="flex flex-col gap-2">
                  <Select
                    value={user.access_status}
                    onValueChange={(value) => handleAccessStatusChange(user.id, value as AccessStatus, user.email)}
                    disabled={updateAccessStatusMutation.isPending}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3" />
                          Active
                        </div>
                      </SelectItem>
                      <SelectItem value="waitlist">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Waitlist
                        </div>
                      </SelectItem>
                      <SelectItem value="suspended">
                        <div className="flex items-center gap-2">
                          <Ban className="h-3 w-3" />
                          Suspended
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {tiers && (
                    <Select
                      value={user.pricing_tier?.name || ''}
                      onValueChange={(value) => handleTierChange(user.id, value, user.email)}
                      disabled={assignTierMutation.isPending}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Set tier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tiers.map((tier) => (
                          <SelectItem key={tier.id} value={tier.name}>
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-3 w-3" />
                              {tier.display_name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    size="sm"
                    variant={user.is_admin ? "destructive" : "default"}
                    onClick={() => handleToggleAdmin(user.id, user.is_admin, user.email)}
                    disabled={toggleAdminMutation.isPending}
                  >
                    {user.is_admin ? (
                      <>
                        <ShieldOff className="h-4 w-4 mr-1" />
                        Remove Admin
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4 mr-1" />
                        Make Admin
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredUsers?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No users match your search</p>
        </div>
      )}
    </div>
  )
}
